import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ShareCard from "../components/ShareCard";

function formatDollar(cents) {
  return "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 });
}

const TIER_LABEL = {
  general: "General Admission",
  vip: "VIP",
  sponsor_table: "Sponsor Table",
  founding_sponsor: "Founding Sponsor",
};

const MAX_POLLS = 30;  // ~60 seconds total (was 20 = 40s)

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, data: null, error: null });
  const { refresh: refreshAuth } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false, data: null, error: "Missing session_id" });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, data, error: null });
          // Refresh auth so pitch_access_granted becomes visible
          if (data.product_type === "founder_pitch_fee") {
            try { await refreshAuth(); } catch (_e) {}
          }
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ loading: false, paid: false, data, error: "Timed out awaiting confirmation." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        if (attempts >= MAX_POLLS) {
          setState({ loading: false, paid: false, data: null, error: "Could not verify payment status." });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshAuth]);

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6 py-20" data-testid="payment-success-page">
      <div className="w-full max-w-2xl text-center">
        {state.loading ? (
          <>
            <Loader2 size={40} className="text-bdu-gold mx-auto animate-spin" />
            <h1 className="mt-6 font-serif text-4xl text-bdu-cream">Confirming your purchase…</h1>
            <p className="mt-3 text-bdu-cream/60">This takes a few seconds while Stripe finalizes the payment.</p>
          </>
        ) : state.paid ? (
          <>
            <CheckCircle2 size={56} className="text-bdu-gold mx-auto" strokeWidth={1.25} />
            {state.data?.product_type === "founder_pitch_fee" ? (
              <>
                <div className="label-eyebrow mt-6">Pitch Access Unlocked</div>
                <h1 className="mt-4 font-serif text-5xl text-bdu-cream tracking-tight">Welcome to the Founders Circle.</h1>
                <p className="mt-4 text-bdu-cream/70 max-w-xl mx-auto">
                  Your $300 has been received. You're now a Black Dollars United member and your
                  pitch admin fee is paid — you can complete your application now.
                </p>
                <div className="mt-10 border border-bdu-gold/20 bg-bdu-surface p-8 text-left" data-testid="payment-success-receipt">
                  <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Receipt</div>
                  <div className="mt-4 grid grid-cols-2 gap-y-4 text-sm">
                    <div className="text-bdu-cream/60">Membership</div>
                    <div className="text-bdu-cream text-right">$100.00</div>
                    <div className="text-bdu-cream/60">Pitch Admin Fee</div>
                    <div className="text-bdu-cream text-right">$200.00</div>
                    <div className="text-bdu-cream/60 pt-3 border-t border-bdu-gold/10">Total Paid</div>
                    <div className="text-bdu-gold text-right font-serif text-xl pt-3 border-t border-bdu-gold/10">
                      {state.data?.amount != null ? formatDollar(state.data.amount) : "$300.00"}
                    </div>
                    <div className="text-bdu-cream/60">Confirmation #</div>
                    <div className="text-bdu-cream/80 text-right text-xs font-mono break-all">{sessionId?.slice(-16)}</div>
                  </div>
                  <p className="mt-6 pt-6 border-t border-bdu-gold/10 text-xs text-bdu-cream/50 leading-relaxed">
                    A receipt is on its way to your email. The admin fee is non-refundable.
                    Complete your application below — our team will review it within 5-7 business days.
                  </p>
                </div>
                <div className="mt-10 flex flex-wrap justify-center gap-3">
                  <Link to="/founders/submit" className="btn-gold" data-testid="success-continue-pitch">
                    Continue to My Application <ArrowRight size={16} />
                  </Link>
                  <Link to="/founders" className="btn-ghost">Browse Founders</Link>
                </div>
              </>
            ) : state.data?.product_type === "pledge" ? (
              <>
                <div className="label-eyebrow mt-6">Donation Received</div>
                <h1 className="mt-4 font-serif text-5xl text-bdu-cream tracking-tight">Thank you — you're building the future.</h1>
                <div className="mt-10 border border-bdu-gold/20 bg-bdu-surface p-8 text-left" data-testid="payment-success-receipt">
                  <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Donation Receipt</div>
                  <div className="mt-4 grid grid-cols-2 gap-y-4 text-sm">
                    <div className="text-bdu-cream/60">Fund</div>
                    <div className="text-bdu-cream text-right">Trillion Dollar Spending Power Fund</div>
                    <div className="text-bdu-cream/60">Amount Donated</div>
                    <div className="text-bdu-gold text-right font-serif text-xl">
                      {state.data?.amount != null ? formatDollar(state.data.amount) : "—"}
                    </div>
                    <div className="text-bdu-cream/60">Confirmation #</div>
                    <div className="text-bdu-cream/80 text-right text-xs font-mono break-all">{sessionId?.slice(-16)}</div>
                  </div>
                  <p className="mt-6 pt-6 border-t border-bdu-gold/10 text-xs text-bdu-cream/50 leading-relaxed">
                    A receipt has been emailed to you. Your contribution has been recorded and will appear on the Roll of Honor if you chose to make it public.
                    Funds are settling into the Citizen Trust Bank account for the Trillion Dollar Spending Power Fund.
                  </p>
                </div>
                <ShareCard
                  sessionId={sessionId}
                  productType="pledge"
                  amountCents={state.data?.amount}
                />
                <div className="mt-10 flex flex-wrap justify-center gap-3">
                  <Link to="/" className="btn-gold" data-testid="success-back-home">
                    Back to Home <ArrowRight size={16} />
                  </Link>
                  <Link to="/pledge" className="btn-ghost">Donate Again</Link>
                </div>
              </>
            ) : state.data?.product_type === "merch" ? (
              <>
                <div className="label-eyebrow mt-6">Order Confirmed</div>
                <h1 className="mt-4 font-serif text-5xl text-bdu-cream tracking-tight">You're on the movement.</h1>
                <p className="mt-4 text-bdu-cream/70 max-w-xl mx-auto">
                  Your order is now printing at our fulfillment partner and will ship in 3–5 business days. You'll get a
                  tracking email the moment it leaves the shop.
                </p>
                <div className="mt-10 border border-bdu-gold/20 bg-bdu-surface p-8 text-left" data-testid="payment-success-receipt">
                  <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Order Receipt</div>
                  <div className="mt-4 grid grid-cols-2 gap-y-4 text-sm">
                    <div className="text-bdu-cream/60">Item</div>
                    <div className="text-bdu-cream text-right">{state.data?.item_name || "BDU Merch"}</div>
                    <div className="text-bdu-cream/60">Total Paid</div>
                    <div className="text-bdu-gold text-right font-serif text-xl">
                      {state.data?.amount != null ? formatDollar(state.data.amount) : "—"}
                    </div>
                    <div className="text-bdu-cream/60">Confirmation #</div>
                    <div className="text-bdu-cream/80 text-right text-xs font-mono break-all">{sessionId?.slice(-16)}</div>
                  </div>
                  <p className="mt-6 pt-6 border-t border-bdu-gold/10 text-xs text-bdu-cream/50 leading-relaxed">
                    Every dollar of profit routes directly into the Citizen Trust Bank account for the Trillion Dollar Spending Power Fund.
                    Every piece of BDU merch is a walking billboard for the movement.
                  </p>
                </div>
                <ShareCard
                  sessionId={sessionId}
                  productType="merch"
                  amountCents={state.data?.amount}
                  itemName={state.data?.item_name}
                />
                <div className="mt-10 flex flex-wrap justify-center gap-3">
                  <Link to="/shop" className="btn-gold" data-testid="success-back-to-shop">
                    Keep Shopping <ArrowRight size={16} />
                  </Link>
                  <Link to="/pledge" className="btn-ghost">Add a Pledge</Link>
                </div>
              </>
            ) : (
              <>
                <div className="label-eyebrow mt-6">Ticket Confirmed</div>
                <h1 className="mt-4 font-serif text-5xl text-bdu-cream tracking-tight">Welcome to the Ball of the Century.</h1>
                <div className="mt-10 border border-bdu-gold/20 bg-bdu-surface p-8 text-left" data-testid="payment-success-receipt">
                  <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>Receipt</div>
                  <div className="mt-4 grid grid-cols-2 gap-y-4 text-sm">
                    <div className="text-bdu-cream/60">Ticket</div>
                    <div className="text-bdu-cream text-right">{TIER_LABEL[state.data?.tier] || state.data?.tier}</div>
                    <div className="text-bdu-cream/60">Quantity</div>
                    <div className="text-bdu-cream text-right">{state.data?.quantity}</div>
                    <div className="text-bdu-cream/60">Total Paid</div>
                    <div className="text-bdu-gold text-right font-serif text-xl">
                      {state.data?.amount != null ? formatDollar(state.data.amount) : "—"}
                    </div>
                    <div className="text-bdu-cream/60">Confirmation #</div>
                    <div className="text-bdu-cream/80 text-right text-xs font-mono break-all">{sessionId?.slice(-16)}</div>
                  </div>
                  <p className="mt-6 pt-6 border-t border-bdu-gold/10 text-xs text-bdu-cream/50 leading-relaxed">
                    A confirmation email is on its way. Ticket purchases are not tax-deductible.
                    Your seat is secured — see you at the Black Royal Unity Ball.
                  </p>
                </div>
                <div className="mt-10 flex flex-wrap justify-center gap-3">
                  <Link to="/events" className="btn-gold" data-testid="success-back-to-events">
                    Back to Events <ArrowRight size={16} />
                  </Link>
                  <Link to="/pledge" className="btn-ghost">Add a Pledge to the Fund</Link>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h1 className="font-serif text-4xl text-bdu-cream">Payment status pending</h1>
            <p className="mt-4 text-bdu-cream/60">{state.error || "Something didn't complete."}</p>
            <div className="mt-8">
              <Link to="/events" className="btn-ghost">Back to Events</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
